import type { ComboboxData, ComboboxItem, RangeSliderProps } from '@mantine/core';
import {
	CloseButton,
	Group,
	MultiSelect,
	NumberInput,
	RangeSlider,
	rem,
	SegmentedControl,
	Select,
	Switch,
	TextInput,
	Tooltip,
} from '@mantine/core';
import { DateInput, DatePickerInput, type DateValue } from '@mantine/dates';
import { useDebouncedCallback } from '@mantine/hooks';
import type { Header, RowData } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { useLogger } from '@apple/utils/logging/useLogger';

import type { DataTableFeatures } from '../features';
import type { ColumnFilterDef, FilterVariant } from '../types';
import { log } from '../utils/logger';

const booleanSchema = z.coerce.boolean().optional();
const numberSchema = z.coerce.number().optional();
const numberTupleSchema = z.tuple([z.coerce.number(), z.coerce.number()]).optional();
const dateSchema = z.union([z.date(), z.literal(''), z.string().date()]).optional(); // Mantine DateInput uses date strings in the format YYYY-MM-DD
const stringSchema = z.coerce.string().optional();
const stringArraySchema = z.array(z.coerce.string()).optional();

export function FilterInput<TData extends RowData, TValue>({
	header,
	label,
}: {
	header: Header<DataTableFeatures, TData, TValue>;
	label: string;
}) {
	const currentFilterValue = header.column.getFilterValue();
	const [localValue, setLocalValue] = useState(currentFilterValue);
	const filterVariant = header.column.columnDef.filter?.variant;
	const isTextFilter = filterVariant === undefined || filterVariant === 'text';
	const isSelectFilter = filterVariant === 'select' || filterVariant === 'multi-select';

	// Update local value when the filter value changes externally, for
	// example clearing all filters via `table.resetColumnFilters()`
	useEffect(() => {
		if (isTextFilter) {
			if (currentFilterValue === undefined) {
				setLocalValue(undefined);
				return;
			}
			return;
		}

		setLocalValue(currentFilterValue);
	}, [currentFilterValue, isTextFilter]);

	const setFilterValueDebounced = useDebouncedCallback(
		(value: unknown) => header.column.setFilterValue(value),
		header.column.columnDef.filter?.debounce ?? getFilterVariantDebounceTime(filterVariant),
	);

	const handleChange = useCallback(
		(value: unknown) => {
			setLocalValue(value);
			setFilterValueDebounced.cancel?.();

			if (isTextFilter && value === '') {
				header.column.setFilterValue(undefined);
				return;
			}

			if (isSelectFilter) {
				header.column.setFilterValue(value);
				return;
			}

			setFilterValueDebounced(value);
		},
		[header.column, isTextFilter, isSelectFilter, setFilterValueDebounced],
	);

	const handleClear = useCallback(() => {
		const table = header.getContext().table;
		const defaultValue = table.initialState.columnFilters.find(
			x => x.id === header.column.id,
		)?.value;

		setFilterValueDebounced.cancel?.();
		setLocalValue(defaultValue);
		header.column.setFilterValue(defaultValue);
	}, [header, setFilterValueDebounced]);

	const clearButton = header.column.getIsFiltered() && (
		<ClearButton
			onClear={() => {
				handleClear();
			}}
		/>
	);

	return filterVariant === 'select' || filterVariant === 'multi-select' ? (
		<SelectFilter
			type={filterVariant}
			id={header.column.id}
			value={localValue}
			filterOptions={header.column.columnDef.filter}
			options={Array.from(header.column.getFacetedUniqueValues().keys())}
			onChange={handleChange}
			onClear={handleClear}
			placeholder={header.column.columnDef.filter?.placeholder}
		/>
	) : filterVariant === 'switch' ? (
		<Switch
			id={header.column.id}
			name={label}
			checked={booleanSchema.parse(localValue)}
			onChange={value => handleChange(value.target.checked)}
			placeholder={header.column.columnDef.filter?.placeholder}
		/>
	) : filterVariant === 'false-only-switch' ? (
		<Switch
			id={header.column.id}
			name={label}
			checked={booleanSchema.parse(localValue === false)}
			onChange={value => handleChange(!value.target.checked ? undefined : false)}
			placeholder={header.column.columnDef.filter?.placeholder}
		/>
	) : filterVariant === 'segmented-switch' ? (
		<SegmentedControl
			id={header.column.id}
			name={label}
			value={stringSchema.parse(localValue) ?? ''}
			onChange={x => handleChange(x === '' ? undefined : x)}
			size='xs'
			fullWidth
			radius='sm'
			color='blue'
			data={Array.from(header.column.getFacetedUniqueValues().keys()).map(x => ({
				value: (x as string) ?? '',
				label: header.column.columnDef.filter?.getFilterDisplayValue?.(x) ?? (x as string),
			}))}
			styles={{
				root: {
					maxHeight: rem(24),
					padding: 0,
				},
				label: {
					height: rem(22),
					lineHeight: rem(18),
				},
			}}
		/>
	) : filterVariant === 'date' ? (
		<DateInput
			id={header.column.id}
			w='100%'
			size='sm'
			clearable
			valueFormat='YYYY-MM-DD'
			value={!localValue ? null : dateSchema.parse(localValue)}
			onChange={handleChange}
			minDate={dateSchema.parse(header.column.columnDef.filter?.minValue)}
			maxDate={dateSchema.parse(header.column.columnDef.filter?.maxValue)}
			defaultDate={dayjs().toDate()}
		/>
	) : filterVariant === 'date-range' ? (
		<DatePickerInput
			type='range'
			numberOfColumns={2}
			id={header.column.id}
			w='100%'
			size='sm'
			clearable
			placeholder={header.column.columnDef.filter?.placeholder}
			valueFormat='YYYY-MM-DD'
			value={
				// When the column filter is cleared, value is set to `undefined` which the date‐picker simply ignores
				// In that case force value to [null, null] so Mantine’s DatePickerInput will reset.
				Array.isArray(localValue)
					? (localValue as [DateValue | null, DateValue | null])
					: [null, null]
			}
			onChange={val => {
				const [min, max] = val;
				// Always reflect whatever the user clicked
				setLocalValue([min, max]);

				// The user has cleared the date range
				if (min === null && max === null) {
					handleChange([null, null]);
					return;
				}

				// Only update the filter once both dates are set
				if (min !== null && max !== null) {
					handleChange([min, max]);
				}
			}}
		/>
	) : filterVariant === 'number' ? (
		<NumberInput
			id={header.column.id}
			w='100%'
			placeholder={header.column.columnDef.filter?.placeholder}
			rightSection={clearButton}
			value={numberSchema.parse(localValue) ?? 0}
			onChange={handleChange}
		/>
	) : filterVariant === 'number-range' ? (
		<Group grow>
			<NumberInput
				id={`${header.column.id}-min`}
				w='50%'
				placeholder={header.column.columnDef.filter?.placeholder}
				rightSection={clearButton}
				value={(localValue as [number, number])?.[0] ?? ''}
				onChange={value => handleChange((old: [number, number]) => [value, old?.[1]])}
			/>
			<NumberInput
				id={`${header.column.id}-max`}
				w='50%'
				placeholder={header.column.columnDef.filter?.placeholder}
				rightSection={clearButton}
				value={(localValue as [number, number])?.[1] ?? ''}
				onChange={value => handleChange((old: [number, number]) => [old?.[0], value])}
			/>
		</Group>
	) : filterVariant === 'number-range-slider' ? (
		<RangeFilter
			id={header.column.id}
			value={numberTupleSchema.parse(localValue)}
			minMaxValues={header.column.getFacetedMinMaxValues()}
			onChange={handleChange}
		/>
	) : (
		<TextInput
			id={header.column.id}
			w='100%'
			placeholder={header.column.columnDef.filter?.placeholder}
			rightSection={clearButton}
			value={stringSchema.parse(localValue) ?? ''}
			onChange={x => handleChange(x.target.value)}
		/>
	);
}

function RangeFilter({
	id,
	value,
	minMaxValues,
	onChange,
}: {
	id?: string;
	value?: [number, number];
	minMaxValues?: [number, number];
	onChange?: (value: [number, number]) => void;
}) {
	const minMaxProps = useMemo<Partial<RangeSliderProps>>(() => {
		const values = {
			min: minMaxValues?.[0] ?? 0,
			max: minMaxValues?.[1] ?? 100,
		};

		values.max = Math.max(values.min, values.max);
		values.min = Math.min(values.min, values.max);

		if (values.min === values.max) {
			values.max = values.min + 1;
		}

		return values;
	}, [minMaxValues]);

	const valueProps = useMemo<Partial<RangeSliderProps>>(() => {
		let minMax: [number, number] = [minMaxProps.min ?? 0, minMaxProps.max ?? 100];

		// Check if filterValue is a valid tuple of numbers
		if (
			Array.isArray(value) &&
			value.length === 2 &&
			typeof value[0] === 'number' &&
			typeof value[1] === 'number'
		) {
			minMax = [value[0], value[1]];
		}

		return {
			value: minMax,
		};
	}, [value, minMaxProps]);

	useLogger({
		log,
		name: `Column ${id} | RangeFilter`,
		props: [
			{
				id,
				value,
				minMaxValues,
				minMaxProps,
				valueProps,
			},
		],
	});

	return (
		<RangeSlider
			id={id}
			w='100%'
			onChange={onChange}
			step={1}
			minRange={1}
			{...minMaxProps}
			{...valueProps}
		/>
	);
}

function SelectFilter<TData extends RowData, TValue = unknown>({
	type,
	id,
	value: filterValue,
	options,
	filterOptions,
	placeholder,
	onChange,
	onClear,
}: {
	type: 'select' | 'multi-select';
	id: string;
	value: TValue | TValue[];
	options: unknown[];
	filterOptions?: ColumnFilterDef<TData, TValue>;
	placeholder?: string;
	onChange: (value: TValue | TValue[]) => void;
	onClear: () => void;
}) {
	const sortedUniqueValues = useMemo<ComboboxData>(
		() =>
			options
				.map(
					x =>
						({
							value: String(x),
							label: filterOptions?.getFilterDisplayValue?.(x) ?? String(x),
						}) satisfies ComboboxItem,
				)
				.sort(filterOptions?.sortCompareFn ?? ((a, b) => a.label.localeCompare(b.label))),
		[filterOptions, options],
	);
	const value = useMemo(
		() => (type !== 'select' ? null : (stringSchema.parse(filterValue) ?? null)),
		[type, filterValue],
	);
	const values = useMemo(
		() => (type !== 'multi-select' ? [] : (stringArraySchema.parse(filterValue) ?? [])),
		[type, filterValue],
	);

	useLogger({
		log,
		name: `Column ${id} | SelectFilter`,
		props: [
			{
				type,
				id,
				rawValue: filterValue,
				parsedValue: value,
				filterOptions,
				rawOptions: options,
				parsedOptions: sortedUniqueValues,
			},
		],
	});

	return type === 'select' ? (
		<Select
			placeholder={placeholder}
			id={id}
			w='100%'
			allowDeselect
			checkIconPosition='right'
			clearable
			value={value}
			onChange={val =>
				!val
					? onClear()
					: onChange(filterOptions?.filterFromString?.(val) ?? (val as TValue))
			}
			onClear={onClear}
			data={sortedUniqueValues}
		/>
	) : (
		<MultiSelect
			placeholder={placeholder}
			id={id}
			w='100%'
			checkIconPosition='right'
			clearable
			styles={{
				pillsList: {
					maxWidth: '80%',
				},
			}}
			value={values}
			data={sortedUniqueValues}
			onChange={stringValues =>
				onChange(
					stringValues.map(
						stringValue =>
							filterOptions?.filterFromString?.(stringValue) ??
							(stringValue as TValue),
					),
				)
			}
			onClear={onClear}
		/>
	);
}

function ClearButton({ onClear }: { onClear?: () => void }) {
	const { t } = useTranslation('controls');
	const label = t('dataTable.resetFilters');
	return (
		<Tooltip label={label}>
			<CloseButton onClick={onClear} aria-label={label} variant='transparent' size='sm' />
		</Tooltip>
	);
}

function getFilterVariantDebounceTime(filterVariant: FilterVariant | undefined) {
	switch (filterVariant) {
		case 'switch':
		case 'date':
		case 'date-range':
			return 50;
		case 'select':
		case 'multi-select':
			return 100;
		case 'number':
		case 'text':
		case 'number-range':
		case 'number-range-slider':
			return 800;
		default:
			return 500;
	}
}
